import React from 'react';
import { container } from 'tsyringe';
import { AuthPresenter } from '../../../../../data/presenters/AuthPresenter';
import history from '../../../../../history';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSignIn, useIsAuthenticated } from 'react-auth-kit';
import { SetLocalStorage } from '../../../../misc/UseLocalStorage';

interface IState {
  isLoading: boolean;
  noAkun: string;
  password: string;
  error: boolean;
  msgError: string;
}

interface InitialState {
  state: IState;
  handleDoLogin: Function;
  changeNip: Function;
  changePassword: Function;
  checkSession: Function;
}

const initialState = {
  state: {
    isLoading: false,
    noAkun: '',
    password: '',
    error: false,
    msgError: '',
  },
  handleDoLogin: () => {},
  changeNip: () => {},
  changePassword: () => {},
  checkSession: () => {},
};
const Context = React.createContext<InitialState>(initialState);
const { Provider: LoginProvider } = Context;

const Provider: React.FC = ({ children }) => {
  const [state, setState] = React.useState<IState>({
    isLoading: false,
    noAkun: '',
    password: '',
    error: false,
    msgError: '',
  });

  // use sign in function
  const signIn = useSignIn();

  // use check session
  const isAuthenticated = useIsAuthenticated();

  const setLoading = (value: boolean) => {
    setState((prevstate) => ({
      ...prevstate,
      isLoading: value,
    }));
  };

  const handleDoLogin = async () => {
    setLoading(true);

    // Clear level from localStorage before login
    localStorage.removeItem('level');

    if (state.noAkun === '') {
      toast.error('Nip harus di isi.', {
        style: {
          boxShadow: '0px 1px 6px #F86E70',
        },
      });
      return;
    }

    if (state.password === '') {
      toast.error('Password harus di isi.', {
        style: {
          boxShadow: '0px 1px 6px #F86E70',
        },
      });
      return;
    }

    try {
      const authPresenter = container.resolve(AuthPresenter);
      const logins = await authPresenter.doLoginPresenter(
        state.noAkun,
        state.password
      );

      const mainData = logins;

      const user = {
        id: mainData.id,
        username: mainData.name,
        nip: mainData?.nip,
        nik: mainData?.nik,
        email: mainData?.email,
        level: mainData?.level,
        jenis_user: mainData?.jenis_user,
      };

      // set image foto
      SetLocalStorage({ name: 'path_photo', value: mainData.path_foto });

      // set level
      SetLocalStorage({ name: 'level', value: mainData?.level });

      // set status pengisian
      SetLocalStorage({
        name: 'status_pengisian',
        value: mainData.status_pengisian,
      });

      if (
        signIn({
          token: mainData.token,
          expiresIn: mainData.expired_in,
          tokenType: 'Bearer',
          authState: user,
        })
      ) {
        const levelToUrl = {
          superadmin: '/admin/dashboard',
          admin: '/admin/dashboard',
          adminhelpdesk: '/admin/dashboard',
          admintte: '/admin/dashboard',
          adminmitrakerjalembaga: '/admin/dashboard',
          adminmitrakerjanonlembaga: '/admin/manajemen_pelatihan',
          peserta: '/',
        };

        const redirectUrl = levelToUrl[mainData?.level];
        if (redirectUrl) {
          history.replace(redirectUrl);
        }

        setLoading(false);
        window.location.reload();
      } else {
        toast.error('Gagal login, silahkan hubungi admin!', {
          style: {
            boxShadow: '0px 1px 6px #F86E70',
          },
        });
        setLoading(false);
        return;
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message?.error, {
        style: {
          boxShadow: '0px 1px 6px #F86E70',
        },
      });
      console.log('error login:', error.response?.data?.message?.error);
      setLoading(false);
      return;
    }
  };

  const changeNip = (noAkun: string) => {
    setState((prevstate) => ({
      ...prevstate,
      noAkun: noAkun,
    }));
    return;
  };

  const changePassword = (password: string) => {
    setState((prevstate) => ({
      ...prevstate,
      password: password,
    }));
    return;
  };

  const checkSession = async () => {
    if (isAuthenticated() !== false) {
      history.replace(`/`);
      window.location.reload();
    } else {
      history.replace('/login');
      window.location.reload();
      toast.error('Session anda telah habis, silahkan login kembali!', {
        style: {
          boxShadow: '0px 1px 6px #F86E70',
        },
      });
    }
  };

  return (
    <LoginProvider
      value={{
        state,
        handleDoLogin,
        changeNip,
        changePassword,
        checkSession,
      }}
    >
      {children}
    </LoginProvider>
  );
};

export const useLoginContext = () => React.useContext(Context);
// eslint-disable-next-line
export default {
  useLoginContext,
  Provider,
};
